<script>
import { mapGetters } from "vuex";
import { resolveUploadUrl } from "@/helpers/api";

/**
 * Letterhead block: company logo + name + address + GSTIN/PAN. Reads the
 * currently-selected company from Vuex (for name/address/GSTIN — these are
 * NOT part of the print profile, they live on the Company entity itself).
 *
 * Logo path resolves through `resolveUploadUrl` so server-side Chromium
 * gets an absolute URL it can actually fetch.
 */
export default {
  name: "PrintHeader",
  inject: { printProfile: { default: () => () => null } },
  computed: {
    ...mapGetters("company", { company: "currentCompany" }),
    profile() { return this.printProfile(); },
    logoUrl() {
      const path = this.profile?.headerLogoPath || this.company?.logoPath;
      return path ? resolveUploadUrl(path) : null;
    },
    addressLines() {
      const c = this.company || {};
      const lines = [];
      if (c.address) lines.push(c.address);
      const cityLine = [c.city, c.pinCode].filter(Boolean).join(" - ");
      if (cityLine) lines.push(cityLine);
      const contact = [c.phone, c.email].filter(Boolean).join("  •  ");
      if (contact) lines.push(contact);
      return lines;
    },
    idLine() {
      const c = this.company || {};
      const parts = [];
      if (c.gstin) parts.push(`GSTIN: ${c.gstin}`);
      if (c.pan) parts.push(`PAN: ${c.pan}`);
      return parts.join("  •  ");
    },
  },
};
</script>

<template>
  <header class="print-header">
    <div class="print-header-logo">
      <img v-if="logoUrl" :src="logoUrl" alt="Logo" />
    </div>
    <div class="print-header-body">
      <div class="print-header-name">{{ company?.name || "" }}</div>
      <div v-for="(line, i) in addressLines" :key="i" class="print-header-line">{{ line }}</div>
      <div v-if="idLine" class="print-header-ids">{{ idLine }}</div>
    </div>
  </header>
</template>

<style scoped>
.print-header {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 4mm;
  align-items: flex-start;
  padding-bottom: 2.5mm;
  border-bottom: 0.5pt solid var(--print-border-strong);
  margin-bottom: 3mm;
}

.print-header-logo img {
  max-height: 18mm;
  max-width: 36mm;
  object-fit: contain;
}

.print-header-name {
  font-weight: 700;
  font-size: var(--print-fs-xl);
  color: var(--print-accent);
  letter-spacing: -0.005em;
  line-height: 1.1;
  margin-bottom: 0.5mm;
}

.print-header-line {
  font-size: var(--print-fs-sm);
  color: var(--print-fg);
  line-height: 1.3;
}

.print-header-ids {
  font-size: var(--print-fs-xs);
  color: var(--print-muted);
  margin-top: 0.5mm;
  font-family: var(--print-font-mono);
  letter-spacing: 0.02em;
}
</style>
